import { useState } from "react"
import { alpha, Box, ButtonBase, Collapse, Stack, Switch, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons"
import { faSparkles } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import {
    SegmentedControlOption,
    StandardSegmentedControl,
} from "../../../../../../../shared/components/segmentedControl/StandardSegmentedControl"

import { useWeekMenu } from "../../WeekMenuContext"
import { AiDaySettings, defaultAiSettings } from "../../weekMenuAiBank"
import { AiSettingsFields } from "./AiSettingsFields"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    subtitle: {
        color: theme.palette.tailwind.gray[500],
    },
    overwriteCard: {
        padding: "1rem 1.25rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: `0 .125rem .375rem 0 ${alpha(theme.palette.common.black, 0.08)}`,
        background: theme.palette.tailwind.purple[50],
    },
    overwriteTitle: {
        fontWeight: 700,
        color: theme.palette.tailwind.purple[800],
    },
    overwriteSubtitle: {
        color: theme.palette.tailwind.slate[600],
    },
    switch: {
        "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.tailwind.purple[600],
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.tailwind.purple[300],
        },
    },
    dayCard: {
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: `0 .125rem .375rem 0 ${alpha(theme.palette.common.black, 0.08)}`,
        overflow: "hidden",
    },
    dayHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "1.25rem 1rem",
        cursor: "pointer",
        textAlign: "left",
    },
    dayChevron: {
        color: theme.palette.tailwind.slate[500],
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
        "& svg": {
            fontSize: "1.25rem",
        },
    },
    dayChevronExpanded: {
        transform: "rotate(180deg)",
    },
    dayName: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[900],
    },
    dayContent: {
        // The card's own padding continues below the divider, so only the top is trimmed.
        padding: "1rem 1rem 1.25rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    footerButton: {
        flex: 1,
    },
}))

enum AiScopeMode {
    ALL = "ALL",
    PER_DAY = "PER_DAY",
}

export type AiSettingsForDay = (dayIndex: number) => AiDaySettings

type Props = Readonly<{
    open: boolean
    onGenerate: (
        settingsForDay: AiSettingsForDay,
        overwriteFilled: boolean,
        includedDayIndexes?: ReadonlySet<number>,
    ) => void
    onClose: () => void
}>

/** Generation settings: one shared setup, or a different one per day. */
export function AiCustomizeDialog({ open, onGenerate, onClose }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { dayLabels } = useWeekMenu()

    const [mode, setMode] = useState(AiScopeMode.ALL)
    const [expandedDay, setExpandedDay] = useState<number | null>(null)
    const [allSettings, setAllSettings] = useState(defaultAiSettings)
    const [perDaySettings, setPerDaySettings] = useState<Readonly<Record<number, AiDaySettings>>>({})
    const [includedDays, setIncludedDays] = useState<ReadonlySet<number>>(
        () => new Set(dayLabels.map((_, dayIndex) => dayIndex)),
    )
    const [overwriteFilled, setOverwriteFilled] = useState(false)

    const modeOptions: ReadonlyArray<SegmentedControlOption<AiScopeMode>> = [
        { value: AiScopeMode.ALL, label: l10n.getString("merchant-week-menu-ai-mode-all") },
        { value: AiScopeMode.PER_DAY, label: l10n.getString("merchant-week-menu-ai-mode-per-day") },
    ]

    // Untouched days follow the shared settings until they are customized.
    const settingsForDay: AiSettingsForDay = (dayIndex) =>
        mode === AiScopeMode.PER_DAY ? (perDaySettings[dayIndex] ?? allSettings) : allSettings

    const handleDaySettingsChange = (dayIndex: number, settings: AiDaySettings) =>
        setPerDaySettings((current) => ({ ...current, [dayIndex]: settings }))

    const toggleDayIncluded = (dayIndex: number) =>
        setIncludedDays((current) => {
            const next = new Set(current)
            if (next.has(dayIndex)) next.delete(dayIndex)
            else next.add(dayIndex)
            return next
        })

    const toggleDayExpanded = (dayIndex: number) =>
        setExpandedDay((current) => (current === dayIndex ? null : dayIndex))

    return (
        <LocalizedStrict id="merchant-week-menu-ai-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="Customize menu generation" onClose={onClose}>
                <Stack spacing="1.5rem" className={classes.content}>
                    <LocalizedStrict id="merchant-week-menu-ai-dialog-subtitle">
                        <Typography variant="body2" align="center" className={classes.subtitle}>
                            Set a theme and rules, then let the AI fill the week
                        </Typography>
                    </LocalizedStrict>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing="1rem"
                        className={classes.overwriteCard}
                    >
                        <Stack>
                            <LocalizedStrict id="merchant-week-menu-ai-overwrite-label">
                                <Typography variant="body2" className={classes.overwriteTitle}>
                                    Overwrite existing dishes
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="merchant-week-menu-ai-overwrite-helper">
                                <Typography variant="caption" className={classes.overwriteSubtitle}>
                                    Off = AI only fills empty slots
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                        <Switch
                            className={classes.switch}
                            checked={overwriteFilled}
                            inputProps={{ "aria-label": l10n.getString("merchant-week-menu-ai-overwrite-label") }}
                            onChange={(event) => setOverwriteFilled(event.target.checked)}
                        />
                    </Stack>

                    <StandardSegmentedControl
                        options={modeOptions}
                        value={mode}
                        onChange={setMode}
                        color="purple"
                        fullWidth
                    />

                    {mode === AiScopeMode.ALL ? (
                        <AiSettingsFields settings={allSettings} onChange={setAllSettings} />
                    ) : (
                        <Stack spacing=".75rem">
                            {dayLabels.map((label, dayIndex) => {
                                const isExpanded = expandedDay === dayIndex

                                return (
                                    <Box key={dayIndex} className={classes.dayCard}>
                                        <ButtonBase
                                            component="div"
                                            aria-expanded={isExpanded}
                                            className={classes.dayHeader}
                                            onClick={() => toggleDayExpanded(dayIndex)}
                                        >
                                            <Stack
                                                component="span"
                                                direction="row"
                                                alignItems="center"
                                                spacing=".75rem"
                                            >
                                                <span
                                                    className={cx(
                                                        classes.dayChevron,
                                                        isExpanded && classes.dayChevronExpanded,
                                                    )}
                                                >
                                                    <FontAwesomeIcon icon={faChevronDown} />
                                                </span>
                                                <Typography component="span" className={classes.dayName}>
                                                    {label}
                                                </Typography>
                                            </Stack>
                                            <Switch
                                                className={classes.switch}
                                                checked={includedDays.has(dayIndex)}
                                                inputProps={{ "aria-label": label }}
                                                onClick={(event) => event.stopPropagation()}
                                                onChange={() => toggleDayIncluded(dayIndex)}
                                            />
                                        </ButtonBase>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box className={classes.dayContent}>
                                                <AiSettingsFields
                                                    settings={perDaySettings[dayIndex] ?? allSettings}
                                                    onChange={(settings) => handleDaySettingsChange(dayIndex, settings)}
                                                />
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )
                            })}
                        </Stack>
                    )}

                    <Stack direction="row" spacing="1rem">
                        <LocalizedStrict id="merchant-week-menu-ai-dialog-cancel-button">
                            <StandardButton
                                fullWidth={false}
                                variant="outlined"
                                color="black"
                                className={classes.footerButton}
                                onClick={onClose}
                            >
                                Cancel
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-week-menu-ai-dialog-generate-button">
                            <StandardButton
                                fullWidth={false}
                                color="black"
                                className={classes.footerButton}
                                startIcon={<FontAwesomeIcon icon={faSparkles} />}
                                onClick={() =>
                                    onGenerate(
                                        settingsForDay,
                                        overwriteFilled,
                                        mode === AiScopeMode.PER_DAY ? includedDays : undefined,
                                    )
                                }
                            >
                                Generate
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
